(() => {
  'use strict';

  const GLOBAL_KEY = '__deebFavoritesRuntimeV1';
  const RUNTIME_VERSION = 3;
  const ENDPOINT = '/apps/deeb-favorites';
  const GUEST_STORAGE_KEY = 'deeb:favorites:guest:v1';
  const BUTTON_SELECTOR = '[data-deeb-favorite-button]';
  const PRODUCT_GID_PATTERN = /^gid:\/\/shopify\/Product\/[1-9][0-9]*$/;

  const existingRuntime = window[GLOBAL_KEY];

  if (existingRuntime) {
    if (existingRuntime.version === RUNTIME_VERSION) {
      if (typeof existingRuntime.refresh === 'function') {
        existingRuntime.refresh(document);
      }

      window.DeebFavorites = existingRuntime;
      return;
    }

    if (typeof existingRuntime.destroy === 'function') {
      existingRuntime.destroy();
    }
  }

  let destroyed = false;
  let domReadyListenerAttached = false;
  let authState = 'unknown';
  let stateReady = false;
  let loadFailed = false;
  let loadPromise = null;

  const favoriteGids = new Set();
  const pendingGids = new Set();

  const normalizeProductGids = (productGids) => {
    if (!Array.isArray(productGids)) {
      return [];
    }

    return Array.from(
      new Set(
        productGids
          .map((productGid) => String(productGid || '').trim())
          .filter((productGid) => PRODUCT_GID_PATTERN.test(productGid))
      )
    );
  };

  const replaceFavoriteGids = (productGids) => {
    favoriteGids.clear();
    normalizeProductGids(productGids).forEach((productGid) => {
      favoriteGids.add(productGid);
    });
  };

  const readGuestFavoriteGids = () => {
    try {
      const storedValue = window.localStorage.getItem(GUEST_STORAGE_KEY);

      if (!storedValue) {
        return [];
      }

      return normalizeProductGids(JSON.parse(storedValue));
    } catch (error) {
      console.warn('Guest Favorites could not be read.', error);
      return [];
    }
  };

  const writeGuestFavoriteGids = (productGids) => {
    const normalizedGids = normalizeProductGids(productGids);

    try {
      window.localStorage.setItem(
        GUEST_STORAGE_KEY,
        JSON.stringify(normalizedGids)
      );
      return normalizedGids;
    } catch (error) {
      console.warn('Guest Favorites could not be saved.', error);
      return null;
    }
  };

  const clearGuestFavoriteGids = () => {
    try {
      window.localStorage.removeItem(GUEST_STORAGE_KEY);
      return true;
    } catch (error) {
      console.warn('Guest Favorites could not be cleared.', error);
      return false;
    }
  };

  const collectButtons = (scope = document) => {
    const buttons = new Set();

    if (
      scope instanceof Element &&
      scope.matches(BUTTON_SELECTOR)
    ) {
      buttons.add(scope);
    }

    if (
      scope &&
      typeof scope.querySelectorAll === 'function'
    ) {
      scope.querySelectorAll(BUTTON_SELECTOR).forEach((button) => {
        buttons.add(button);
      });
    }

    return Array.from(buttons);
  };

  const hasButtons = (scope = document) =>
    collectButtons(scope).length > 0;

  const getProductGid = (button) =>
    String(button?.dataset?.productGid || '').trim();

  const getLabel = (button, key, fallback) =>
    String(button?.dataset?.[key] || fallback);

  const updateButton = (button) => {
    const productGid = getProductGid(button);
    const label = button.querySelector('[data-deeb-favorite-label]');

    if (!PRODUCT_GID_PATTERN.test(productGid)) {
      button.disabled = true;
      button.classList.remove('is-saved');
      button.setAttribute('aria-pressed', 'false');
      button.setAttribute('aria-busy', 'false');
      button.dataset.deebFavoriteState = 'invalid';
      return;
    }

    const addLabel = getLabel(
      button,
      'deebFavoriteAddLabel',
      'Add to Favorites'
    );
    const savedLabel = getLabel(
      button,
      'deebFavoriteSavedLabel',
      'In Favorites'
    );
    const loadingLabel = getLabel(
      button,
      'deebFavoriteLoadingLabel',
      'Loading Favorites'
    );
    const addProductLabel = getLabel(
      button,
      'deebFavoriteAddProductLabel',
      'Add this product to Favorites'
    );
    const removeProductLabel = getLabel(
      button,
      'deebFavoriteRemoveProductLabel',
      'Remove this product from Favorites'
    );
    const errorProductLabel = getLabel(
      button,
      'deebFavoriteErrorProductLabel',
      'Favorites are temporarily unavailable'
    );

    if (authState === 'guest') {
      const saved = favoriteGids.has(productGid);

      button.disabled = false;
      button.classList.toggle('is-saved', saved);
      button.setAttribute('aria-pressed', String(saved));
      button.setAttribute('aria-busy', 'false');
      button.setAttribute(
        'aria-label',
        saved ? removeProductLabel : addProductLabel
      );
      button.dataset.deebFavoriteState = saved
        ? 'guest-saved'
        : 'guest-not-saved';

      if (label) {
        label.textContent = saved ? savedLabel : addLabel;
      }

      return;
    }

    const pending = pendingGids.has(productGid);

    if (!stateReady || authState !== 'authenticated') {
      const loading = loadPromise !== null;

      button.disabled = loading || !loadFailed;
      button.classList.remove('is-saved');
      button.setAttribute('aria-pressed', 'false');
      button.setAttribute('aria-busy', loading ? 'true' : 'false');
      button.setAttribute(
        'aria-label',
        loadFailed ? errorProductLabel : loadingLabel
      );
      button.dataset.deebFavoriteState = loadFailed
        ? 'error'
        : 'loading';

      if (label) {
        label.textContent = loadFailed ? addLabel : loadingLabel;
      }

      return;
    }

    const saved = favoriteGids.has(productGid);

    button.disabled = pending;
    button.classList.toggle('is-saved', saved);
    button.setAttribute('aria-pressed', String(saved));
    button.setAttribute('aria-busy', pending ? 'true' : 'false');
    button.setAttribute(
      'aria-label',
      saved ? removeProductLabel : addProductLabel
    );
    button.dataset.deebFavoriteState = pending
      ? 'pending'
      : saved
        ? 'saved'
        : 'not-saved';

    if (label) {
      label.textContent = saved ? savedLabel : addLabel;
    }
  };

  const refreshButtons = (scope = document) => {
    collectButtons(scope).forEach(updateButton);
  };

  const readJson = async (response) => {
    try {
      return await response.json();
    } catch {
      return null;
    }
  };

  const requestFavoriteGids = async () => {
    const response = await fetch(ENDPOINT, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
      },
      credentials: 'same-origin',
      cache: 'no-store',
    });

    if (response.status === 401) {
      return { authenticated: false, productGids: [] };
    }

    if (!response.ok) {
      throw new Error(`Favorites GET failed with HTTP ${response.status}`);
    }

    const payload = await readJson(response);

    if (!payload || !Array.isArray(payload.favorites)) {
      throw new Error('Favorites GET returned an invalid payload');
    }

    const productGids = payload.favorites.map((favorite) =>
      String(favorite?.productGid || '').trim()
    );

    if (productGids.some((gid) => !PRODUCT_GID_PATTERN.test(gid))) {
      throw new Error('Favorites GET returned an invalid productGid');
    }

    return {
      authenticated: true,
      productGids: normalizeProductGids(productGids),
    };
  };

  const loadFavorites = ({ force = false } = {}) => {
    if (destroyed || !hasButtons(document)) {
      return Promise.resolve(false);
    }

    if (loadPromise) {
      return loadPromise;
    }

    if (stateReady && !force) {
      refreshButtons(document);
      return Promise.resolve(true);
    }

    authState = 'unknown';
    stateReady = false;
    loadFailed = false;

    loadPromise = (async () => {
      const serverState = await requestFavoriteGids();

      if (!serverState.authenticated) {
        replaceFavoriteGids(readGuestFavoriteGids());
        authState = 'guest';
        stateReady = true;
        loadFailed = false;
        return true;
      }

      replaceFavoriteGids(serverState.productGids);
      authState = 'authenticated';
      stateReady = true;
      loadFailed = false;

      const guestGids = readGuestFavoriteGids();

      if (guestGids.length > 0) {
        const mergeSucceeded = await mergeGuestFavorites(guestGids);

        if (!mergeSucceeded) {
          if (authState === 'guest') {
            replaceFavoriteGids(readGuestFavoriteGids());
          }

          return true;
        }

        const refreshedState = await requestFavoriteGids();

        if (!refreshedState.authenticated) {
          replaceFavoriteGids(readGuestFavoriteGids());
          authState = 'guest';
          return true;
        }

        replaceFavoriteGids(refreshedState.productGids);
        clearGuestFavoriteGids();
      }

      return true;
    })()
      .catch((error) => {
        authState = 'unknown';
        stateReady = false;
        loadFailed = true;
        console.warn('Deeb Favorites could not be loaded.', error);
        return false;
      })
      .finally(() => {
        loadPromise = null;
        refreshButtons(document);
      });

    refreshButtons(document);
    return loadPromise;
  };

  const mutateFavorite = async (method, productGid) => {
    const response = await fetch(ENDPOINT, {
      method,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      credentials: 'same-origin',
      cache: 'no-store',
      body: JSON.stringify({ productGid }),
    });

    if (response.status === 401) {
      replaceFavoriteGids(readGuestFavoriteGids());
      authState = 'guest';
      stateReady = true;
      return { authRequired: true, ok: false };
    }

    const payload = await readJson(response);

    if (!response.ok) {
      const message =
        payload && typeof payload.error === 'string'
          ? payload.error
          : `Favorites ${method} failed with HTTP ${response.status}`;
      throw new Error(message);
    }

    if (method === 'POST') {
      if (payload?.favorite?.productGid !== productGid) {
        throw new Error('Favorites POST returned an invalid payload');
      }
    } else if (
      payload?.productGid !== productGid ||
      typeof payload?.removed !== 'boolean'
    ) {
      throw new Error('Favorites DELETE returned an invalid payload');
    }

    return { authRequired: false, ok: true };
  };

  async function mergeGuestFavorites(guestGids) {
    const productGidsToMerge = normalizeProductGids(guestGids).filter(
      (productGid) => !favoriteGids.has(productGid)
    );

    for (const productGid of productGidsToMerge) {
      try {
        const result = await mutateFavorite('POST', productGid);

        if (result.authRequired || !result.ok) {
          return false;
        }
      } catch (error) {
        console.warn('Guest Favorites could not be merged.', error);
        return false;
      }
    }

    return true;
  }

  const dispatchChange = (productGid, saved) => {
    document.dispatchEvent(
      new CustomEvent('deeb:favorites:change', {
        detail: {
          productGid,
          saved,
          productGids: Array.from(favoriteGids),
        },
      })
    );
  };

  const ensureLoaded = async () => {
    if (stateReady) {
      return true;
    }

    return loadFavorites({ force: true });
  };

  const setGuestFavorite = (productGid, saved) => {
    const guestGids = new Set(readGuestFavoriteGids());

    if (saved) {
      guestGids.add(productGid);
    } else {
      guestGids.delete(productGid);
    }

    const updatedGids = writeGuestFavoriteGids(Array.from(guestGids));

    if (!updatedGids) {
      return false;
    }

    replaceFavoriteGids(updatedGids);
    refreshButtons(document);
    dispatchChange(productGid, saved);
    return true;
  };

  const setFavorite = async (productGid, saved) => {
    const normalizedGid = String(productGid || '').trim();

    if (!PRODUCT_GID_PATTERN.test(normalizedGid)) {
      return false;
    }

    const loaded = await ensureLoaded();

    if (!loaded) {
      return false;
    }

    if (authState === 'guest') {
      return setGuestFavorite(normalizedGid, saved);
    }

    if (authState !== 'authenticated') {
      return false;
    }

    if (favoriteGids.has(normalizedGid) === saved) {
      refreshButtons(document);
      return true;
    }

    if (pendingGids.has(normalizedGid)) {
      return false;
    }

    pendingGids.add(normalizedGid);
    refreshButtons(document);

    try {
      const result = await mutateFavorite(
        saved ? 'POST' : 'DELETE',
        normalizedGid
      );

      if (result.authRequired) {
        return setGuestFavorite(normalizedGid, saved);
      }

      if (saved) {
        favoriteGids.add(normalizedGid);
      } else {
        favoriteGids.delete(normalizedGid);
      }

      dispatchChange(normalizedGid, saved);
      return true;
    } catch (error) {
      console.warn('Deeb Favorites could not be updated.', error);
      return false;
    } finally {
      pendingGids.delete(normalizedGid);
      refreshButtons(document);
    }
  };

  const toggleButton = async (button) => {
    const productGid = getProductGid(button);

    if (!PRODUCT_GID_PATTERN.test(productGid)) {
      return;
    }

    const loaded = await ensureLoaded();

    if (!loaded) {
      return;
    }

    if (authState === 'guest') {
      setGuestFavorite(
        productGid,
        !favoriteGids.has(productGid)
      );
      return;
    }

    if (authState !== 'authenticated') {
      return;
    }

    await setFavorite(
      productGid,
      !favoriteGids.has(productGid)
    );
  };

  const handleDocumentClick = (event) => {
    if (!(event.target instanceof Element)) {
      return;
    }

    const button = event.target.closest(BUTTON_SELECTOR);

    if (!button) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    void toggleButton(button);
  };

  const runtime = {
    version: RUNTIME_VERSION,
    endpoint: ENDPOINT,
    guestStorageKey: GUEST_STORAGE_KEY,

    getProductGids() {
      return Array.from(favoriteGids);
    },

    has(productGid) {
      return (
        stateReady &&
        favoriteGids.has(String(productGid || '').trim())
      );
    },

    async add(productGid) {
      return setFavorite(productGid, true);
    },

    async remove(productGid) {
      return setFavorite(productGid, false);
    },

    async reload() {
      return loadFavorites({ force: true });
    },

    refresh(scope = document) {
      refreshButtons(scope);

      if (!stateReady && !loadPromise && hasButtons(document)) {
        void loadFavorites();
      }
    },

    destroy() {
      if (destroyed) {
        return;
      }

      destroyed = true;

      document.removeEventListener('click', handleDocumentClick);
      document.removeEventListener('shopify:section:load', handleSectionLoad);
      document.removeEventListener('deeb:wishlist:refresh', handleRefreshEvent);
      document.removeEventListener('deeb:favorites:refresh', handleRefreshEvent);
      window.removeEventListener('pageshow', handlePageShow);
      window.removeEventListener('storage', handleStorage);

      if (domReadyListenerAttached) {
        document.removeEventListener('DOMContentLoaded', handleDomContentLoaded);
        domReadyListenerAttached = false;
      }

      if (window[GLOBAL_KEY] === runtime) {
        delete window[GLOBAL_KEY];
      }

      if (window.DeebFavorites === runtime) {
        delete window.DeebFavorites;
      }
    },
  };

  function handleSectionLoad(event) {
    if (destroyed) return;
    runtime.refresh(event.target);
  }

  function handleRefreshEvent(event) {
    if (destroyed) return;
    runtime.refresh(event.detail?.scope || document);
  }

  function handlePageShow(event) {
    if (destroyed) return;

    if (event.persisted && hasButtons(document)) {
      void loadFavorites({ force: true });
    } else {
      runtime.refresh(document);
    }
  }

  function handleStorage(event) {
    if (
      destroyed ||
      authState !== 'guest' ||
      event.storageArea !== window.localStorage ||
      event.key !== GUEST_STORAGE_KEY
    ) {
      return;
    }

    replaceFavoriteGids(readGuestFavoriteGids());
    refreshButtons(document);
  }

  function handleDomContentLoaded() {
    domReadyListenerAttached = false;

    if (destroyed) return;
    runtime.refresh(document);
  }

  const startRuntime = () => {
    document.addEventListener('click', handleDocumentClick);
    document.addEventListener('shopify:section:load', handleSectionLoad);
    document.addEventListener('deeb:wishlist:refresh', handleRefreshEvent);
    document.addEventListener('deeb:favorites:refresh', handleRefreshEvent);
    window.addEventListener('pageshow', handlePageShow);
    window.addEventListener('storage', handleStorage);

    if (document.readyState === 'loading') {
      domReadyListenerAttached = true;
      document.addEventListener('DOMContentLoaded', handleDomContentLoaded, {
        once: true,
      });
    } else {
      runtime.refresh(document);
    }
  };

  window[GLOBAL_KEY] = runtime;
  window.DeebFavorites = runtime;

  startRuntime();
})();
